const en = {
  // Navbar
  nav: {
    features: "Features",
    pricing: "Pricing",
    getStarted: "Get Started",
    signIn: "Sign In",
    profile: "Profile",
    atsCheck: "Free ATS check",
    atsCheckBadge: "Free",
    requestDemo: "Request a demo",
  },

  // Hero
  hero: {
    badge: "AI-Powered Interview Prep",
    titleLine1: "Find Your Weak Spots",
    titleLine2: "Before the Interviewer Does",
    subtitle: "Upload your resume for instant AI feedback on strengths and gaps. Then run a full adaptive voice interview tailored to your target role, with deep readiness analytics.",
    subtitleSignedIn: "Paste the job description — we'll tailor the questions to your resume and launch an adaptive voice interview with deep readiness analytics.",
    placeholder: "Paste job description text here...",
    analyzing: "Preparing interview...",
    analyzeBtn: "Start interview",
    analyzingHint: "This can take up to 20 seconds for long descriptions.",
    tryAgain: "Try again",
    trustPrivate: "100% Private",
    trustFaang: "FAANG Calibration",
    trustVoice: "Voice Interview",
  },

  // JD section for signed-in users (above JD textarea)
  jdSection: {
    title: "Paste a job description — start your interview",
    subtitle: "We'll extract the key competencies from the JD and launch a voice interview calibrated to your experience.",
  },

  // Preview Questions
  preview: {
    title: "3 Free Bar-Raiser Questions",
    competenciesFound: "competencies found",
    hard: "Hard",
    medium: "Medium",
    behavioral: "Behavioral",
    startBtn: "🎙️ Start Full Voice Interview",
    startDesc: "Unlock the adaptive AI interviewer that finds your knowledge boundaries",
    questionPrefix: "Deep-dive question on",
  },

  // Features
  features: {
    voiceTitle: "Real-time Voice Interview",
    voiceDesc: "Speak naturally. Our AI listens, understands, and probes deeper — just like a real senior interviewer.",
    boundaryTitle: "Knowledge Boundary Detection",
    boundaryDesc: "The AI doesn't stop at surface answers. It iteratively digs until it finds exactly where your knowledge ends.",
    analyticsTitle: "Deep Readiness Analytics",
    analyticsDesc: "Radar charts, gap analysis, model answers, and a shareable Readiness Score for your LinkedIn.",
  },

  // Pricing
  pricing: {
    title: "Simple, Transparent Pricing",
    subtitle: "One analysis is free. Unlock full deep-dive interviews for less than a coffee meeting.",
    freeTitle: "Free Preview",
    freeFeature1: "1 JD analysis",
    freeFeature2: "3 bar-raiser questions",
    freeFeature3: "Full voice interview",
    freeFeature4: "Readiness report",
    currentPlan: "Current Plan",
    paidTitle: "Full Access",
    paidPrice: "$5",
    paidSub: "3 full deep-dive interviews",
    paidFeature1: "Unlimited JD analyses",
    paidFeature2: "3 full voice interviews",
    paidFeature3: "Detailed readiness reports",
    paidFeature4: "LinkedIn-shareable score",
    paidBtn: "Get Full Access",
    mostPopular: "MOST POPULAR",
  },

  // Social proof
  social: {
    trustedBy: "Engineers prep for interviews at 100+ companies, including",
  },

  // Footer
  footer: {
    copyright: "© 2026 HireFit. All rights reserved.",
    privacy: "Privacy",
    terms: "Terms",
    contact: "Contact",
    forBusinesses: "For businesses",
  },

  // For Businesses landing page (/for-companies)
  forCompanies: {
    title: "Hire smarter with AI voice interviews",
    subtitle:
      "Create a vacancy, share a link, and let our AI run a real voice interview with every candidate. You get back a structured report — scored competencies, full transcript, and a downloadable CV — without an hour on a Zoom call.",
    step1Title: "Create a vacancy",
    step1Desc:
      "Paste the job description, pick the interview duration and candidate level. We turn it into a competency list automatically.",
    step2Title: "Share your candidate link",
    step2Desc:
      "Each vacancy gets a public URL. Candidates upload a CV, leave their contact info, and jump straight into a live AI voice interview — no signup needed.",
    step3Title: "Review reports in your CRM",
    step3Desc:
      "Every finished interview lands as a card with competency scores, verdict, full transcript, and the resume PDF. Move candidates through Advanced or Rejected with one click — rejected candidates even get a polite feedback email automatically.",
    howItWorks: "How it works",
    ctaButton: "Open your CRM →",
    ctaNote: "Sign up or sign in with your work email. Free to start.",
    back: "← Back to the interview prep",
  },

  // B2B pricing (public table on /for-companies + CRM billing page)
  b2bPricing: {
    title: "Pricing that scales with your hiring",
    subtitle:
      "Every plan includes AI voice interviews, structured reports, and the candidate CRM. Start free and upgrade as you grow.",
    perMonth: "/mo",
    free: "Free",
    custom: "Custom",
    mostPopular: "Most popular",
    currentPlan: "Current plan",
    subscribe: "Subscribe",
    getStarted: "Get started",
    contactSales: "Contact sales",
    yourPlan: "Your plan",
    interviewsThisMonth: "Interviews this month",
    activeVacancies: "Active vacancies",
    unlimited: "Unlimited",
    renews: "Renews",
    upgradeSuccess: "You're all set — your plan is now active.",
    upgradeCanceled: "Checkout canceled. No changes were made.",
    vacancyLimitReached: "You've reached your plan's vacancy limit. Upgrade to add more.",
    plans: {
      starter: {
        name: "Starter",
        tagline: "Small companies / trial",
        features: [
          "50 AI interviews / mo",
          "1 active vacancy",
          "Structured reports & transcripts",
          "Candidate CRM",
        ],
      },
      pro: {
        name: "Pro",
        tagline: "Mid business / recruiters",
        features: [
          "100 AI interviews / mo",
          "Up to 3 vacancies",
          "Everything in Starter",
          "Email notifications",
        ],
      },
      team: {
        name: "Team",
        tagline: "Recruiting agencies",
        features: [
          "300 AI interviews / mo",
          "Up to 10 vacancies",
          "Everything in Pro",
          "Priority processing",
        ],
      },
      enterprise: {
        name: "Enterprise",
        tagline: "Corporations / large HR",
        features: [
          "Unlimited interviews & vacancies",
          "Customization",
          "Priority support",
          "Dedicated onboarding",
        ],
      },
    },
  },

  // ATS check (/ats-check)
  atsCheck: {
    title: "Free ATS resume check",
    subtitle:
      "Get a hiring-manager-grade audit of your resume in 15 seconds. Paste a job description too and we'll show you which keywords you're missing and where your experience falls short.",
    signedOutCta: "Sign in to run a free ATS check",
    signedOutHint: "We need to sign you in to track your 5 free checks per 24 hours.",
    resumeSectionTitle: "1. Your resume",
    tabFile: "Upload file",
    tabText: "Paste text",
    fileCta: "Drag & drop or click to upload",
    fileHint: "PDF or DOCX, up to 10 MB",
    fileTooLarge: "File too large (10 MB max).",
    fileWrongType: "Only PDF or DOCX files are supported.",
    textPlaceholder: "Paste the full text of your resume here...",
    jdSectionTitle: "2. Job description (optional)",
    jdSectionHint:
      "Add a JD and we'll also show you exactly which keywords you're missing and where you have experience gaps.",
    jdPlaceholder: "Paste the job description here to get keyword match and gap analysis...",
    analyzeBtn: "Analyze my resume",
    analyzing: "Analyzing your resume...",
    quotaLabel: "Free checks used today: {used}/{limit}",
    newCheckBtn: "Run another check",
    resultTitle: "Your ATS analysis",
    scoreOutOf: "out of 100",
    scoreExcellent: "Excellent — ATS-ready",
    scoreGood: "Good — minor polish needed",
    scoreAverage: "Average — real improvements needed",
    scoreBelow: "Below threshold — likely filtered out",
    issuesTitle: "Issues to fix",
    noIssues: "No major issues detected.",
    severityHigh: "High",
    severityMedium: "Medium",
    severityLow: "Low",
    categoryFormatting: "Formatting",
    categoryKeywords: "Keywords",
    categoryStructure: "Structure",
    categoryClarity: "Clarity",
    categoryQuantification: "Quantification",
    categoryExperience: "Experience",
    categoryContact: "Contact",
    bulletsTitle: "Rewritten bullet points",
    bulletsHint: "Stronger versions with metrics and impact — copy and paste them straight in.",
    bulletOriginal: "Your version",
    bulletImproved: "Improved",
    bulletReason: "Why it's better",
    copy: "Copy",
    copied: "Copied!",
    keywordMatchTitle: "Keyword match",
    keywordScoreLabel: "Match score",
    keywordMatched: "Matched in your resume",
    keywordMissing: "Missing from your resume",
    keywordSuggestions: "How to improve",
    gapTitle: "Gap analysis",
    gapAligned: "What aligns",
    gapGaps: "What's missing",
    gapRecommendations: "Recommendations",
    missingResume: "Please upload or paste your resume first.",
    rateLimited: "Daily limit reached. You can run 5 checks per 24 hours — come back tomorrow.",
    genericError: "Something went wrong. Please try again.",
    tryFullInterview: "Want a full voice interview next?",
    tryFullInterviewDesc:
      "Put your resume to the test with an adaptive AI interview tailored to your target role.",
    tryFullInterviewBtn: "Start interview prep →",
  },

  // Free trial banner
  trial: {
    daysLeft: "{n} days left",
    freeInterviews: "{n} free interviews",
    expired: "Free trial expired",
    buyNow: "Buy interviews",
  },

  // Auth & quota
  quota: {
    signInToStart: "Sign in to start",
    noInterviewsRemaining: "No interviews remaining",
    buyInterviews: "Buy interviews",
    buyMore: "Buy more interviews — $5",
    checkingQuota: "Checking access...",
  },

  // Paywall modal (legacy — used by hillel page)
  paywall: {
    title: "Unlock Full Interview",
    desc: "Get a complete adaptive voice interview with deep readiness analytics for $5 (3 interviews).",
    btn: "Get Full Access — $5",
    alreadyPaid: "Already paid? Enter your email:",
    emailPlaceholder: "your@email.com",
    check: "Check",
    tryFree: "Try free (5 min demo)",
    allUsed: "All interviews used. Purchase again for more.",
    notFound: "No purchase found for this email.",
    checkFailed: "Failed to check. Try again.",
    havePromo: "Have a promo code?",
    promoPlaceholder: "APEX-XXXXXX",
    redeemBtn: "Redeem",
    promoSuccess: "Promo applied! Starting interview...",
  },

  // Interview Chat
  chat: {
    topicsToCover: "📋 Topics to Cover",
    standardMode: "Standard Mode",
    liveMode: "⚡ LIVE Mode (Beta)",
    beginInterview: "Begin Interview",
    backBtn: "Back",
    voiceInterview: "Voice Interview",
    liveInterview: "⚡ Live Interview",
    endInterview: "End Interview",
    you: "You",
    interviewer: "Interviewer",
    aiSpeaking: "🔊 AI is speaking...",
    aiListening: "🎤 Listening...",
    processing: "Processing your voice...",
    thinking: "Thinking...",
    liveSpeak: "⚡ Live — speak naturally",
    connecting: "Connecting...",
    disconnected: "Disconnected",
    timeWarning: "remaining in free demo. Pay to unlock full interviews.",
    timeEndingSoon: "remaining — the interview will wrap up shortly.",
    timeCutoff: "⏱️ Free demo time is up. Generating your partial report...",
    audioError: "⚠️ Oops, something broke. We're already on it — please try again in a few seconds.",
    retryBtn: "Retry",
    dismissBtn: "Dismiss",
    startMessage: "Start interview.",
    doneSpeakingBtn: "I'm done",
    micListening: "Listening",
    cameraPromptTitle: "Enable Your Camera",
    cameraPromptDesc: "We'd like to record your face during the interview. This helps evaluate non-verbal communication and ensures interview integrity. Your camera is optional but recommended.",
    cameraActive: "Camera is on",
    cameraDenied: "Camera access was denied",
    cameraRequesting: "Requesting access...",
    cameraEnable: "Enable Camera",
    cameraStartInterview: "Start Interview with Camera",
    cameraSkip: "Skip — continue without camera",
    gdprTitle: "Data Processing Consent",
    gdprDesc: "During this interview, we will record and process your voice responses using AI to evaluate your technical competencies. You may also be asked to enable your webcam and share your screen. Your data is processed in accordance with GDPR and our Privacy Policy.",
    gdprPoint1: "Your voice will be recorded and transcribed for AI analysis",
    gdprPoint2: "Your webcam and screen may be recorded to ensure interview integrity",
    gdprPoint3: "Your responses will be used to generate a readiness report",
    gdprPoint4: "You can request data deletion at any time",
    gdprAccept: "I consent — continue",
    gdprDecline: "Decline",
    gdprPrivacyLink: "Read our Privacy Policy",
  },

  // Report
  report: {
    title: "Readiness Report",
    subtitle: "AI Deep-Dive Interview Assessment",
    shareBtn: "Share to LinkedIn",
    shareCopied: "Copied! Paste in LinkedIn",
    overallReadiness: "Overall Readiness",
    readyScreening: "Ready for Screening",
    needsPrep: "Needs More Prep",
    skillBreakdown: "Skill Breakdown",
    you: "You",
    passingBar: "Passing Bar",
    focusArea: "Focus Area",
    criticalGaps: "Critical Knowledge Gaps",
    areasNeedImprovement: "areas need improvement",
    aiAnalysis: "AI Analysis",
    modelAnswer: "Model Answer",
    majorMiss: "Major Miss",
    needsWork: "Needs Work",
    redFlags: "Red Flags",
    studyPlan: "Recommended Study Plan",
    anotherRound: "Ready for Another Round?",
    anotherRoundDesc: "Practice makes perfect. Start a new interview to improve your weak areas and boost your readiness score.",
    startNew: "Start New Interview",
    shareTitle: "Results Copied!",
    shareDesc: "Your interview results are in your clipboard. Press",
    shareDescCtrl: "Ctrl+V",
    shareDescOr: "or",
    shareDescCmd: "Cmd+V",
    shareDescEnd: "to paste them into your LinkedIn post.",
    openLinkedIn: "Open LinkedIn",
  },

  // Profile page
  profile: {
    title: "My Profile",
    interviews: "Interview History",
    noInterviews: "No interviews yet",
    remaining: "interviews remaining",
    buyMore: "Buy +3 interviews ($5)",
    verdict: "Verdict",
    viewReport: "View Report",
    jobDescription: "Job Description",
    competencies: "Competency Scores",
    redFlags: "Red Flags",
    studyPlan: "Study Plan",
    loading: "Loading profile...",
    resume: "My Resume",
    resumeInfo: "Your resume will be saved and may be shared with partner companies",
    uploadResume: "Upload Resume",
    uploadedAt: "Uploaded",
    fileTooLarge: "File too large (max 10 MB)",
    invalidFileType: "Only PDF and DOCX files are allowed",
  },

  // Loading states
  loading: {
    verifyingPayment: "Verifying your payment...",
    generatingReport: "Generating your readiness report...",
  },

  reportSkeleton: {
    title: "Putting together your report",
    subtitle: "A few more seconds — we're analysing the interview and scoring each skill.",
    hint: "Keep this tab open — the report will appear here automatically.",
    stages: [
      "Reading your conversation...",
      "Scoring competencies...",
      "Spotting weak areas...",
      "Drafting your study plan...",
      "Finishing up...",
    ],
  },

  // Language switcher
  lang: {
    en: "EN",
    ru: "RU",
    cz: "CZ",
  },

  // Resume Input (resume-first landing)
  resumeInput: {
    tabUpload: "Upload PDF",
    tabPaste: "Paste Text",
    uploadBtn: "Upload Resume",
    dragDropHint: "Drag & drop your PDF or DOCX here, or click to browse",
    pastePlaceholder: "Paste your resume text here...",
    analyzeBtn: "Analyze Resume",
    analyzing: "Analyzing...",
    fileTooLarge: "File too large. Maximum size is 10 MB.",
    invalidFileType: "Invalid file type. Please upload a PDF or DOCX file.",
  },

  // Resume Feedback (resume-first landing)
  resumeFeedback: {
    sectionTitle: "Resume Analysis",
    scoreLabel: "Resume Score",
    strengthsTitle: "Strengths",
    improvementsTitle: "Areas for Improvement",
    overallAssessmentTitle: "Overall Assessment",
  },

  // Post-Analysis CTA section (resume-first landing)
  postAnalysis: {
    ctaHeading: "Want a mock interview based on your resume?",
    ctaSubtext: "Our AI interviewer will ask targeted questions based on your experience and skills — just like the real thing.",
    signInBtn: "Sign in to start",
    freeTrialText: "3 free interviews after signup",
    pricingInfo: "$5 for 3 more interviews",
    feature1: "Voice interview with real-time AI feedback",
    feature2: "Knowledge boundary detection",
    feature3: "Deep readiness analytics & report",
  },

  // Resume status (Landing for signed-in users)
  resumeStatus: {
    attached: "Your resume",
    uploadedAt: "Updated",
    changeBtn: "Change",
    noResume: "No resume uploaded yet",
    noResumeHint: "Add your resume so interview questions match your experience",
    uploadBtn: "Upload",
    uploadTitle: "Upload resume",
    replaceTitle: "Replace resume",
    cancel: "Cancel",
    syncing: "Saving your resume...",
    syncingHint: "This takes a few seconds — we're extracting the text for the interview.",
  },

  // Success page
  success: {
    title: "Payment Successful!",
    desc: "You now have access to 3 full deep-dive interviews.",
    email: "Account",
    startBtn: "Start Interview",
    errorTitle: "Something went wrong",
    errorDesc: "We couldn't verify your payment. If you already paid, enter your email on the main page.",
    goHome: "Go to Home",
  },

  // B2B CRM
  crm: {
    nav: {
      homeAria: "HireFit CRM — home",
      billing: "Billing",
      signIn: "Sign In",
    },
    landing: {
      badge: "AI hiring platform",
      title: "Hire smarter with AI voice interviews.",
      subtitle:
        "Create a vacancy, share a link, and let HireFit run a real voice interview with every candidate — you get back a scored report, full transcript, and CV. Without an hour on Zoom.",
      openCrm: "Open your CRM",
      seeHow: "See how it works",
      trustFreeToStart: "Free to start",
      trustNoCard: "No credit card",
      trustSetup: "Set up in 2 min",
      feature1Title: "Create Vacancies in Minutes",
      feature1Desc:
        "Paste your job description, add custom screening questions, and get a branded interview page with a unique URL — ready to share with candidates.",
      feature2Title: "AI Interviews Candidates 24/7",
      feature2Desc:
        "Our AI interviewer conducts structured technical and behavioral interviews based on your job requirements — no scheduling needed.",
      feature3Title: "Track Results & Compare",
      feature3Desc:
        "Get detailed competency scores, red flags, and study plans for every candidate. Compare applicants side-by-side in your dashboard.",
      feature4Title: "Schedule Calls Instantly",
      feature4Desc:
        "Send Calendly invites directly from the candidate profile. Leave notes, track progress, and move top talent forward — all in one place.",
      sectionTitle: "Everything you need to hire smarter",
      sectionSubtitle:
        "From vacancy creation to candidate evaluation — streamline your entire hiring pipeline with AI.",
      signInCta: "Sign In to Get Started",
    },
    dashboard: {
      title: "Vacancies",
      subtitle: "Manage your job postings and track candidates",
      newVacancy: "New Vacancy",
      loading: "Loading vacancies…",
      retry: "Retry",
      emptyTitle: "No vacancies yet",
      emptyDesc: "Create your first vacancy to start interviewing candidates with AI.",
      emptyCta: "Create Your First Vacancy",
      colVacancy: "Vacancy",
      colCreated: "Created",
      colCandidates: "Candidates",
      colPublicUrl: "Public URL",
      colActions: "Actions",
      copyUrl: "Copy URL",
      copied: "Copied!",
      deleteVacancy: "Delete vacancy",
      deleteConfirm: "Delete this vacancy? Candidates will no longer be able to access it.",
    },
    vacancy: {
      loadingCandidates: "Loading candidates…",
      backToVacancies: "Back to Vacancies",
      backToAdmin: "Back to Admin",
      edit: "Edit",
      editVacancy: "Edit Vacancy",
      updatedSuccess: "Vacancy updated successfully!",
      title: "Title *",
      jobDescription: "Job Description *",
      duration: "Duration",
      level: "Level",
      languages: "Languages",
      resultEmails: "Result Emails",
      interviewFocus: "Interview Focus",
      interviewAvoid: "Interview Avoid",
      saving: "Saving…",
      saveChanges: "Save Changes",
      cancel: "Cancel",
      statTotalCandidates: "Total Candidates",
      statAverageScore: "Average Score",
      statReadiness: "Readiness",
      filterAll: "All",
      filterNew: "New",
      filterInProgress: "In progress",
      filterAdvanced: "Advanced",
      filterRejected: "Rejected",
      noCandidatesWithStatus: "No candidates with this status.",
      emptyTitle: "No candidates yet",
      emptyDesc: "Share the vacancy link with candidates to start receiving interview results.",
      colCandidate: "Candidate",
      colStatus: "Status",
      colCountry: "Country",
      colInterviewDate: "Interview Date",
      colAvgScore: "Avg Score",
      colVerdict: "Verdict",
      colContacts: "Contacts",
      colFiles: "Files",
      fileScreenRecording: "Screen recording",
      fileWebcamRecording: "Webcam recording",
    },
    candidate: {
      loading: "Loading candidate…",
      backToCandidates: "Back to Candidates",
      back: "Back",
      screenRecording: "Screen recording",
      webcamRecording: "Webcam recording",
      statusLabel: "Status:",
      avgScore: "avg score",
      competencyBreakdown: "Competency Breakdown",
      modelAnswer: "Model answer",
      translating: "Translating report from English…",
      redFlags: "Red Flags",
      studyPlan: "Recommended Study Plan",
      overallVerdict: "Overall Verdict",
      notes: "Notes",
      scheduleCall: "Schedule a Call",
      calendlyLink: "Calendly Link",
      personalMessage: "Personal Message",
      rejectTitle: "Reject Candidate",
      rejectPrompt: "Optionally add a rejection message:",
      rejectPlaceholder: "Reason for rejection (optional)...",
      confirmRejection: "Confirm Rejection",
      cancel: "Cancel",
    },
    status: {
      new: "new",
      advanced: "advanced",
      rejected: "rejected",
      inProgress: "in progress",
    },
    newVacancy: {
      back: "Back to Dashboard",
      heading: "Create New Vacancy",
      subheading: "Set up a job posting and let AI interview your candidates automatically.",
      companyLabel: "Company Name",
      companyPlaceholder: "e.g. Acme Corp",
      companyHelper: "This will be used to generate your unique vacancy URL.",
      titleLabel: "Vacancy Title",
      titlePlaceholder: "e.g. Senior Frontend Developer",
      jdLabel: "Job Description",
      cleanUp: "Clean up",
      cleaning: "Cleaning…",
      jdPlaceholder: "Paste the job description here. Use the 'Clean up' button to remove formatting artifacts.",
      jdHelper: "The AI will use this to generate interview questions. Clean up removes formatting artifacts from copy-paste.",
      customQuestionsLabel: "Custom Interview Questions",
      customQuestionsHelper: "Add questions the AI interviewer should ask. Choose whether each question is asked before or after the technical assessment.",
      questionPlaceholder: "Enter a question…",
      removeQuestion: "Remove question",
      addQuestion: "Add Question",
      askLabel: "Ask:",
      askBefore: "Before interview",
      askAfter: "After interview",
      customFieldPlaceholders: [
        "Where are you located?",
        "What salary are you looking for?",
        "When can you start?",
        "Are you open to relocation?",
        "Do you have experience with remote work?",
        "What is your notice period?",
        "Are you authorized to work in this country?",
        "What are your preferred working hours?",
        "Do you have any upcoming travel plans?",
        "What languages do you speak fluently?",
      ],
      durationLabel: "Interview Duration",
      durationHelper: "Controls the depth and number of questions the AI interviewer will ask.",
      durationScreening: "15 min (Screening)",
      duration30: "30 min",
      duration45: "45 min",
      duration60: "1 hour",
      screeningBadge: "Screening",
      levelLabel: "Position Level",
      levelHelper: "The AI will calibrate question difficulty and scoring based on the expected level.",
      levelIntern: "Intern",
      levelJunior: "Junior",
      levelMiddle: "Middle",
      levelSenior: "Senior",
      levelLead: "Lead",
      levelPrincipal: "Principal",
      languagesLabel: "Interview Languages",
      languagesHelper: "Candidates will be able to choose one of these languages when starting the interview.",
      langEn: "English",
      langRu: "Russian",
      langCz: "Czech",
      optional: "(optional)",
      focusLabel: "Focus On",
      focusPlaceholder: "e.g. Ask about experience with ATS systems, Boolean search, closing technical roles, working with hiring managers...",
      focusHelper: "Guide the AI on what topics to cover. Leave empty and the AI will decide based on the job description.",
      avoidLabel: "Avoid Asking",
      avoidPlaceholder: "e.g. Don't ask coding or system design questions — this is a non-technical role...",
      avoidHelper: "Tell the AI what to skip. Useful for non-technical roles where the job description mentions tech as context, not as a requirement.",
      resultEmailsLabel: "Result Emails",
      resultEmailsPlaceholder: "email1@example.com, email2@example.com",
      resultEmailsHelper: "Optional. Interview results will be sent to these addresses in addition to your account email.",
      submit: "Create Vacancy",
      submitting: "Creating Vacancy…",
      successTitle: "Vacancy Created!",
      successSubtitle: "Share this link with candidates to start interviewing:",
      copy: "Copy",
      copied: "Copied!",
      viewCandidates: "View Candidates",
      errCompanyRequired: "Company name is required",
      errMaxQuestions: "Maximum 10 custom questions allowed",
      errQuestionTooLong: "Question {n} exceeds 200 characters",
      errEmptyQuestion: "Question {n} is empty — type it in or remove it.",
      suggestionsLabel: "Suggestions (click to add):",
      errInvalidEmails: "Invalid email(s): {emails}",
      errCleanFailed: "Failed to clean job description",
      errCreateFailed: "Failed to create vacancy",
    },
  },
} as const;

export default en;
